/**
 * SpecificityTargetsConfig.jsx
 *
 * Editor for specificity_targets.yaml. Follows the same shell convention as
 * the other configuration pages (Sider + navy Header with breadcrumb +
 * Lock/Save).
 *
 * Body: card with a left list of families (E11, N18, F32, ...) and a right
 * editor for the selected family. The selected family's evidence_sections
 * is drag-reorderable (same dnd-kit pattern as EvidenceHintsConfig); other
 * lists use Select-tags or custom row editors as appropriate to their
 * shape.
 *
 * Page lands locked. Click Lock → unlock → all fields, list operations, and
 * the drag handles become available. Save is a placeholder; all edits are
 * in-memory.
 *
 * Data: fetched from /rules/specificity_targets.yaml.
 *
 * Dependencies:
 *   - js-yaml
 *   - @dnd-kit/core, @dnd-kit/sortable, @dnd-kit/utilities (already
 *     installed for EvidenceHintsConfig)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout, Typography, Input, Button, Tooltip, Avatar, Empty, Tag,
  Spin, Alert, Popconfirm, Modal, Form, AutoComplete, Switch,
  Select, message,
} from 'antd';
import {
  UserOutlined, LogoutOutlined, LockOutlined, UnlockOutlined,
  SaveOutlined, PlusOutlined, DeleteOutlined, SearchOutlined,
  HolderOutlined, CloseOutlined,
} from '@ant-design/icons';
import yaml from 'js-yaml';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import EnvVersionLabel from './Envversionlabel';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

const SPECIFICITY_TARGETS_URL = '/rules/specificity_targets.yaml';


const EDITABLE_ROLES = ['hcc_reviewer'];



// Known section names — same set as the other config pages. AutoComplete
// is free-text so the user can also type a custom value.
const KNOWN_SECTION_NAMES = [
  'assessment_plan',
  'pmh',
  'problem_list',
  'hpi',
  'physical_exam',
  'medications',
  'labs',
  'social_history',
  'family_history',
  'ros',
  'objective',
  'subjective',
  'clinical_history',
  'discharge_summary',
  'other_clinical_notes',
];

// ─── SortableSectionRow — one draggable row in the evidence_sections list ─
const SortableSectionRow = ({ id, value, onDelete, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: 6,
    padding: '8px 10px',
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <span
        {...listeners}
        style={{
          cursor: disabled ? 'default' : 'grab',
          color: disabled ? '#D1D5DB' : '#6B7280',
          padding: '2px 4px',
          touchAction: 'none',
        }}
        title={disabled ? 'Unlock to reorder' : 'Drag to reorder'}
      >
        <HolderOutlined />
      </span>

      <span style={{
        flex: 1,
        fontFamily: 'monospace',
        fontSize: 13,
        color: '#111827',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {value}
      </span>

      {!disabled && onDelete && (
        <Button
          size="small"
          type="text"
          icon={<CloseOutlined style={{ color: '#DC2626' }} />}
          onClick={onDelete}
        />
      )}
    </div>
  );
};

// ─── EvidenceSectionsEditor — drag-reorderable list + add row ──────────
const EvidenceSectionsEditor = ({ sections, onChange, editMode }) => {
  const [addInput, setAddInput] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = sections.indexOf(active.id);
      const newIndex = sections.indexOf(over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onChange(arrayMove(sections, oldIndex, newIndex));
      }
    }
  };

  const handleDeleteAt = (i) => {
    onChange(sections.filter((_, idx) => idx !== i));
  };

  const handleAdd = () => {
    const v = addInput.trim();
    if (!v) return;
    if (sections.includes(v)) {
      message.warning(`"${v}" is already in the list`);
      return;
    }
    onChange([...sections, v]);
    setAddInput('');
  };

  // De-dup section IDs so dnd-kit gets unique keys (defensive — YAML may
  // technically carry duplicates).
  const uniqueIds = useMemo(() => {
    const seen = new Set();
    return sections.map((s) => {
      let id = s;
      let n = 1;
      while (seen.has(id)) { id = `${s}__${++n}`; }
      seen.add(id);
      return id;
    });
  }, [sections]);

  const autocompleteOptions = KNOWN_SECTION_NAMES
    .filter(s => !sections.includes(s))
    .filter(s => !addInput || s.includes(addInput.toLowerCase()))
    .map(s => ({ value: s }));

  return (
    <div>
      {sections.length === 0 ? (
        <Text style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic', display: 'block', marginBottom: 10 }}>
          No sections defined.
        </Text>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={uniqueIds} strategy={verticalListSortingStrategy}>
            {sections.map((s, idx) => (
              <SortableSectionRow
                key={uniqueIds[idx]}
                id={uniqueIds[idx]}
                value={s}
                disabled={!editMode}
                onDelete={editMode ? () => handleDeleteAt(idx) : null}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      {editMode && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <AutoComplete
            value={addInput}
            onChange={setAddInput}
            options={autocompleteOptions}
            placeholder="Section name (e.g., pmh, labs)"
            style={{ flex: 1, fontFamily: 'monospace' }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            filterOption={false}
          />
          <Button
            icon={<PlusOutlined />}
            onClick={handleAdd}
            disabled={!addInput.trim()}
            style={{
              backgroundColor: addInput.trim() ? '#162349' : undefined,
              color: addInput.trim() ? 'white' : undefined,
            }}
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
};

// ─── Section heading helper ─────────────────────────────────────────────
const FieldLabel = ({ text, count }) => (
  <Text style={{
    fontSize: 11, fontWeight: 700, color: '#9CA3AF',
    letterSpacing: '0.08em', textTransform: 'uppercase',
    display: 'block', marginBottom: 8,
  }}>
    {text}{count !== undefined ? ` (${count})` : ''}
  </Text>
);

// ─── CandidateSiblingsEditor — list of {code, description} rows ─────────
const CandidateSiblingsEditor = ({ siblings, onChange, editMode }) => {
  const handleUpdate = (idx, field, value) => {
    onChange(siblings.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };
  const handleDelete = (idx) => {
    onChange(siblings.filter((_, i) => i !== idx));
  };
  const handleAdd = () => {
    onChange([...siblings, { code: '', description: '' }]);
  };

  if (siblings.length === 0 && !editMode) {
    return (
      <Text style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>
        No candidate siblings defined.
      </Text>
    );
  }

  return (
    <div>
      {siblings.length === 0 ? (
        <Text style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic', display: 'block', marginBottom: 10 }}>
          No candidate siblings yet. Click "Add sibling" to start.
        </Text>
      ) : editMode ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {siblings.map((s, idx) => (
            <div key={idx} style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              padding: '8px 10px',
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: 6,
            }}>
              <Input
                value={s.code}
                onChange={e => handleUpdate(idx, 'code', e.target.value)}
                placeholder="Code"
                style={{
                  width: 110,
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              />
              <TextArea
                value={s.description}
                onChange={e => handleUpdate(idx, 'description', e.target.value)}
                placeholder="Description for this sibling code"
                autoSize={{ minRows: 1, maxRows: 4 }}
                style={{ flex: 1 }}
              />
              <Button
                icon={<CloseOutlined />}
                onClick={() => handleDelete(idx)}
                danger
              />
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {siblings.map((s, idx) => (
            <div key={idx} style={{
              display: 'flex',
              gap: 10,
              padding: '6px 0',
              borderBottom: idx < siblings.length - 1 ? '1px solid #F3F4F6' : 'none',
            }}>
              <span style={{
                fontFamily: 'monospace',
                fontSize: 13,
                fontWeight: 700,
                color: '#162349',
                background: '#EEF2FF',
                border: '1px solid #C7D2FE',
                borderRadius: 4,
                padding: '2px 8px',
                height: 'fit-content',
                flexShrink: 0,
              }}>
                {s.code}
              </span>
              <Text style={{ fontSize: 14, color: '#1F2937', flex: 1 }}>
                {s.description}
              </Text>
            </div>
          ))}
        </div>
      )}

      {editMode && (
        <Button
          icon={<PlusOutlined />}
          onClick={handleAdd}
          size="small"
          style={{ marginTop: 8 }}
        >
          Add sibling
        </Button>
      )}
    </div>
  );
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────
const SpecificityTargetsConfig = ({
  username,
  sessionId,
  userRole,
  userFeatures,
  tabOrder,
  onLogout,
}) => {
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────────────
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState(null);
  const [yamlData, setYamlData]     = useState(null);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [editMode, setEditMode]     = useState(false);

  // Add-new modal
  const [addModalOpen, setAddModalOpen]   = useState(false);
  const [newFamilyId, setNewFamilyId]     = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newError, setNewError]           = useState(null);

  const canEdit = EDITABLE_ROLES.includes(userRole);


  // ── Load ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    fetch(SPECIFICITY_TARGETS_URL)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then(text => {
        if (cancelled) return;
        const parsed = yaml.load(text) || {};
        const families = Array.isArray(parsed.families) ? parsed.families : [];
        // Normalize each family so all expected fields exist.
        const normalized = families.map(f => ({
          family: f.family || '',
          description: f.description || '',
          require_keyword_match: !!f.require_keyword_match,
          trigger_codes: Array.isArray(f.trigger_codes) ? f.trigger_codes : [],
          candidate_siblings: Array.isArray(f.candidate_siblings)
            ? f.candidate_siblings.map(s => ({
                code: s.code || '',
                description: s.description || '',
              }))
            : [],
          evidence_sections: Array.isArray(f.evidence_sections) ? f.evidence_sections : [],
          screener_keywords: Array.isArray(f.screener_keywords) ? f.screener_keywords : [],
          note: f.note || '',
        }));
        setYamlData({
          namespace: parsed.namespace || '',
          version: parsed.version || '',
          families: normalized,
        });
        if (normalized.length > 0) setSelectedFamily(normalized[0].family);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load specificity_targets.yaml:', err);
        setLoadError(err.message || 'Unknown error');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // ── Derived ──────────────────────────────────────────────────────
  const familyIds = useMemo(
    () => yamlData?.families.map(f => f.family) ?? [],
    [yamlData]
  );

  const filteredFamilyIds = useMemo(() => {
    if (!yamlData) return [];
    if (!searchText.trim()) return familyIds;
    const q = searchText.toLowerCase();
    return familyIds.filter(id => {
      if (id.toLowerCase().includes(q)) return true;
      const fam = yamlData.families.find(f => f.family === id);
      return (fam?.description || '').toLowerCase().includes(q);
    });
  }, [familyIds, searchText, yamlData]);

  const selectedEntry = useMemo(() => {
    if (!selectedFamily || !yamlData) return null;
    return yamlData.families.find(f => f.family === selectedFamily) || null;
  }, [selectedFamily, yamlData]);

  // ── Handlers ─────────────────────────────────────────────────────
  const handleLockToggle = () => setEditMode(prev => !prev);
  const handleSave = () => {
    message.info('Save is not yet wired up — edits are in memory only.');
  };

  const updateSelectedFamily = useCallback((patch) => {
    if (!selectedFamily || !yamlData) return;
    setYamlData(prev => ({
      ...prev,
      families: prev.families.map(f =>
        f.family === selectedFamily ? { ...f, ...patch } : f
      ),
    }));
  }, [selectedFamily, yamlData]);

  const handleDeleteFamily = (id) => {
    setYamlData(prev => ({
      ...prev,
      families: prev.families.filter(f => f.family !== id),
    }));
    if (selectedFamily === id) {
      const remaining = familyIds.filter(f => f !== id);
      setSelectedFamily(remaining[0] || null);
    }
  };

  // Add-new
  const openAddModal = () => {
    setNewFamilyId('');
    setNewDescription('');
    setNewError(null);
    setAddModalOpen(true);
  };

  const handleAddSubmit = () => {
    const id = newFamilyId.trim();
    if (!id) {
      setNewError('Family ID is required.');
      return;
    }
    if (familyIds.includes(id)) {
      setNewError(`Family "${id}" already exists.`);
      return;
    }
    setYamlData(prev => ({
      ...prev,
      families: [
        ...prev.families,
        {
          family: id,
          description: newDescription.trim(),
          require_keyword_match: false,
          trigger_codes: [],
          candidate_siblings: [],
          evidence_sections: [],
          screener_keywords: [],
          note: '',
        },
      ],
    }));
    setSelectedFamily(id);
    setAddModalOpen(false);
  };

  // ── Render ───────────────────────────────────────────────────────
  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Sider ──────────────────────────────────────────────── */}
      <Sider
        width={60}
        collapsible
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{
          display: 'flex', flexDirection: 'column',
          height: '100vh', position: 'fixed',
          left: 0, top: 0, bottom: 0, zIndex: 10,
        }}
      >
        <div style={{
          display: 'flex',
          background: 'linear-gradient(180deg, #162349 0%, #2a4a7c 100%)',
          flexDirection: 'column', justifyContent: 'space-between',
          height: '100%', alignItems: 'center', padding: '20px 0',
        }}>
          <div></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
            <Tooltip title={username} placement="right">
              <Avatar
                style={{ backgroundColor: 'white', color: '#162349', cursor: 'pointer', border: '2px solid white' }}
                icon={<UserOutlined />}
              />
            </Tooltip>
            <Tooltip title="Logout" placement="right">
              <Button
                type="text"
                icon={<LogoutOutlined style={{ color: 'white' }} />}
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>
            <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 'Bold' }} />
          </div>
        </div>
      </Sider>

      <Layout style={{ marginLeft: 60 }}>

        {/* ── Header ────────────────────────────────────────────── */}
        <Header style={{
          background: '#162349',
          padding: '0 20px',
          height: 64,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
            <span
              onClick={() => navigate('/hcc-config')}
              style={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: 20, fontWeight: 700,
                cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = 'white')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
            >
              HCC STUDIO
            </span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }}>/</span>
            <span style={{
              color: 'white', fontSize: 20, fontWeight: 700,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              Specificity Targets
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {canEdit ? (
              <>
                <Tooltip title={editMode ? 'Click to lock (return to view-only)' : 'Click to unlock for editing'}>
                  <Button
                    icon={editMode ? <UnlockOutlined /> : <LockOutlined />}
                    onClick={handleLockToggle}
                    style={{
                      backgroundColor: editMode ? '#FEF3C7' : 'white',
                      color: '#162349',
                      fontWeight: 600,
                      borderColor: editMode ? '#F59E0B' : '#d9d9d9',
                    }}
                  >
                    {editMode ? 'Unlocked' : 'Locked'}
                  </Button>
                </Tooltip>
                <Tooltip title={editMode ? 'Save (not yet wired up)' : 'Unlock to enable saving'}>
                  <Button
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    disabled={!editMode}
                    style={{ backgroundColor: 'white', color: '#162349', fontWeight: 600 }}
                  >
                    Save
                  </Button>
                </Tooltip>
              </>
            ) : (
              <Tag
                style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontWeight: 600,
                  fontSize: 13,
                  padding: '4px 12px',
                }}
              >
                Display Only
              </Tag>
            )}
          </div>
          
        </Header>

        {/* ── Content ───────────────────────────────────────────── */}
        <Content style={{
          padding: '28px 32px',
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)',
          minHeight: 'calc(100vh - 64px)',
        }}>

          {loading && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#6B7280', fontSize: 14 }}>
                Loading specificity targets…
              </div>
            </div>
          )}

          {!loading && loadError && (
            <Alert
              type="warning"
              showIcon
              message="Configuration file not found"
              description={<span>Could not load <code>{SPECIFICITY_TARGETS_URL}</code>. ({loadError})</span>}
              style={{ maxWidth: 640, margin: '40px auto' }}
            />
          )}

          {!loading && !loadError && yamlData && (
            <>
              {/* Metadata strip */}
              <div style={{
                background: 'rgba(255,255,255,0.6)',
                border: '1px solid #E5E7EB',
                borderRadius: 8,
                padding: '10px 16px',
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 24,
              }}>
                <Text style={{ fontSize: 13, color: '#374151' }}>
                  <Text strong>Namespace:</Text>{' '}
                  <code style={{ fontFamily: 'monospace' }}>{yamlData.namespace || '—'}</code>
                </Text>
                <Text style={{ fontSize: 13, color: '#374151' }}>
                  <Text strong>Version:</Text>{' '}
                  <code style={{ fontFamily: 'monospace' }}>{yamlData.version || '—'}</code>
                </Text>
                <Text style={{ fontSize: 13, color: '#374151' }}>
                  <Text strong>Families:</Text> {yamlData.families.length}
                </Text>
              </div>

              <div style={{
                background: 'white',
                borderRadius: 8,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                display: 'flex',
                minHeight: 'calc(100vh - 64px - 56px - 80px)',
              }}>

                {/* ── Left: family list ───────────────────────── */}
                <div style={{
                  width: 320, flexShrink: 0,
                  borderRight: '1px solid #E5E7EB',
                  display: 'flex', flexDirection: 'column',
                  background: '#FAFAFA',
                }}>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid #E5E7EB' }}>
                    <Tooltip title={editMode ? '' : 'Unlock to add a new family'}>
                      <Button
                        icon={<PlusOutlined />}
                        onClick={openAddModal}
                        disabled={!editMode}
                        block
                        style={{
                          backgroundColor: editMode ? '#162349' : undefined,
                          color: editMode ? 'white' : undefined,
                          fontWeight: 600,
                        }}
                      >
                        Add new family
                      </Button>
                    </Tooltip>
                  </div>

                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
                    <Input
                      prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
                      placeholder="Search family or description…"
                      value={searchText}
                      onChange={e => setSearchText(e.target.value)}
                      allowClear
                    />
                  </div>

                  <div style={{ flex: 1, overflowY: 'auto' }}>
                    {filteredFamilyIds.length === 0 ? (
                      <div style={{ padding: '40px 16px', textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {searchText ? `No families match "${searchText}"` : 'No families defined.'}
                        </Text>
                      </div>
                    ) : (
                      filteredFamilyIds.map(id => {
                        const isSelected = id === selectedFamily;
                        const fam = yamlData.families.find(f => f.family === id);
                        return (
                          <div
                            key={id}
                            onClick={() => setSelectedFamily(id)}
                            style={{
                              padding: '10px 16px',
                              cursor: 'pointer',
                              background: isSelected ? '#E0E7FF' : 'transparent',
                              borderLeft: isSelected ? '3px solid #162349' : '3px solid transparent',
                              borderBottom: '1px solid #F0F0F0',
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 8,
                              transition: 'background 0.12s',
                            }}
                            onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#F0F4FF'; }}
                            onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                          >
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <span style={{
                                fontFamily: 'monospace',
                                fontSize: 14,
                                fontWeight: 700,
                                color: '#162349',
                                display: 'block',
                              }}>
                                {id}
                              </span>
                              <span style={{
                                fontSize: 12,
                                color: '#6B7280',
                                display: 'block',
                                marginTop: 2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                                {fam?.description || <em style={{ color: '#D1D5DB' }}>(no description)</em>}
                              </span>
                              <span style={{
                                fontSize: 11,
                                color: '#9CA3AF',
                                display: 'block',
                                marginTop: 2,
                              }}>
                                {fam?.trigger_codes?.length || 0} trigger{(fam?.trigger_codes?.length || 0) === 1 ? '' : 's'} ·
                                {' '}{fam?.candidate_siblings?.length || 0} sibling{(fam?.candidate_siblings?.length || 0) === 1 ? '' : 's'}
                              </span>
                            </div>

                            {editMode && (
                              <Popconfirm
                                title="Delete this family?"
                                description={`Remove "${id}" and all its triggers, siblings, and keywords?`}
                                onConfirm={(e) => { if (e?.stopPropagation) e.stopPropagation(); handleDeleteFamily(id); }}
                                onCancel={(e) => { if (e?.stopPropagation) e.stopPropagation(); }}
                                okText="Delete"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                              >
                                <Button
                                  size="small"
                                  type="text"
                                  icon={<DeleteOutlined style={{ color: '#DC2626' }} />}
                                  onClick={e => e.stopPropagation()}
                                />
                              </Popconfirm>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div style={{ padding: '10px 16px', borderTop: '1px solid #E5E7EB', background: 'white' }}>
                    <Text style={{ fontSize: 11, color: '#9CA3AF' }}>
                      {filteredFamilyIds.length} of {familyIds.length} families
                    </Text>
                  </div>
                </div>

                {/* ── Right: family editor ────────────────────── */}
                <div style={{ flex: 1, padding: '24px 32px', overflowY: 'auto' }}>
                  {!selectedEntry ? (
                    <div style={{ padding: '80px 0', textAlign: 'center' }}>
                      <Empty description="Select a family to view its specificity targets." />
                    </div>
                  ) : (
                    <>
                      {/* Family heading */}
                      <div style={{ marginBottom: 24 }}>
                        <FieldLabel text="Family" />
                        <Title level={3} style={{ margin: '4px 0 0 0', fontFamily: 'monospace', color: '#111827' }}>
                          {selectedEntry.family}
                        </Title>
                      </div>

                      {/* Description */}
                      <div style={{ marginBottom: 24 }}>
                        <FieldLabel text="Description" />
                        {editMode ? (
                          <TextArea
                            value={selectedEntry.description}
                            onChange={e => updateSelectedFamily({ description: e.target.value })}
                            placeholder="Short description of the family"
                            autoSize={{ minRows: 1, maxRows: 4 }}
                          />
                        ) : (
                          <Text style={{ fontSize: 14, color: '#1F2937', display: 'block', whiteSpace: 'pre-wrap' }}>
                            {selectedEntry.description || <span style={{ color: '#D1D5DB', fontStyle: 'italic' }}>(no description)</span>}
                          </Text>
                        )}
                      </div>

                      {/* require_keyword_match */}
                      <div style={{ marginBottom: 24, padding: '12px 14px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <Switch
                            checked={selectedEntry.require_keyword_match}
                            disabled={!editMode}
                            onChange={(checked) => updateSelectedFamily({ require_keyword_match: checked })}
                          />
                          <Text style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                            Require keyword match (cost gate)
                          </Text>
                        </div>
                        <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginTop: 6, marginLeft: 56 }}>
                          When ON, the LLM call is skipped unless at least one screener keyword appears in the
                          gathered evidence. When OFF, the LLM is always invoked when a trigger code is present.
                        </Text>
                      </div>

                      {/* Trigger codes */}
                      <div style={{ marginBottom: 24 }}>
                        <FieldLabel text="Trigger codes" count={selectedEntry.trigger_codes.length} />
                        <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 8 }}>
                          Codes already in the encounter's coded_findings that activate this refinement pass.
                        </Text>
                        {editMode ? (
                          <Select
                            mode="tags"
                            value={selectedEntry.trigger_codes}
                            onChange={vals => updateSelectedFamily({ trigger_codes: vals })}
                            tokenSeparators={[',']}
                            style={{ width: '100%', fontFamily: 'monospace' }}
                            placeholder="Add a trigger code (e.g., E11.9) and press Enter"
                          />
                        ) : (
                          selectedEntry.trigger_codes.length === 0 ? (
                            <Text style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>
                              No trigger codes defined.
                            </Text>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {selectedEntry.trigger_codes.map((c, idx) => (
                                <span key={idx} style={{
                                  fontFamily: 'monospace',
                                  fontWeight: 700,
                                  fontSize: 13,
                                  color: '#162349',
                                  background: '#EEF2FF',
                                  border: '1px solid #C7D2FE',
                                  borderRadius: 4,
                                  padding: '2px 8px',
                                }}>
                                  {c}
                                </span>
                              ))}
                            </div>
                          )
                        )}
                      </div>

                      {/* Candidate siblings */}
                      <div style={{ marginBottom: 24 }}>
                        <FieldLabel text="Candidate siblings" count={selectedEntry.candidate_siblings.length} />
                        <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 8 }}>
                          The specific codes the LLM may suggest as replacements. Any suggestion outside this list is rejected.
                        </Text>
                        <CandidateSiblingsEditor
                          siblings={selectedEntry.candidate_siblings}
                          onChange={vals => updateSelectedFamily({ candidate_siblings: vals })}
                          editMode={editMode}
                        />
                      </div>

                      {/* Evidence sections (drag-reorder) */}
                      <div style={{ marginBottom: 24 }}>
                        <FieldLabel text={`Evidence sections (${selectedEntry.evidence_sections.length})`} />
                        <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 8 }}>
                          Section types to pull evidence text from.
                          {editMode ? ' Drag the handle to reorder.' : ' Top of list is searched first. Unlock to edit or reorder.'}
                        </Text>
                        <EvidenceSectionsEditor
                          sections={selectedEntry.evidence_sections}
                          onChange={vals => updateSelectedFamily({ evidence_sections: vals })}
                          editMode={editMode}
                        />
                      </div>

                      {/* Screener keywords */}
                      <div style={{ marginBottom: 24 }}>
                        <FieldLabel text="Screener keywords" count={selectedEntry.screener_keywords.length} />
                        <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 8 }}>
                          Fast substring scan. If none of these appear in the gathered evidence, the LLM call is skipped (cost gate).
                          Be conservative — these are a cost gate, not a clinical gate.
                        </Text>
                        {editMode ? (
                          <Select
                            mode="tags"
                            value={selectedEntry.screener_keywords}
                            onChange={vals => updateSelectedFamily({ screener_keywords: vals })}
                            tokenSeparators={[',']}
                            style={{ width: '100%' }}
                            placeholder="Add a keyword and press Enter"
                          />
                        ) : (
                          selectedEntry.screener_keywords.length === 0 ? (
                            <Text style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>
                              No screener keywords defined.
                            </Text>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {selectedEntry.screener_keywords.map((k, idx) => (
                                <span key={idx} style={{
                                  fontSize: 12,
                                  color: '#374151',
                                  background: '#F3F4F6',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: 12,
                                  padding: '3px 10px',
                                }}>
                                  {k}
                                </span>
                              ))}
                            </div>
                          )
                        )}
                      </div>

                      {/* Note */}
                      <div>
                        <FieldLabel text="Note (LLM prompt guidance)" />
                        <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 8 }}>
                          Clinical guidance shown verbatim to the LLM in the prompt.
                        </Text>
                        {editMode ? (
                          <TextArea
                            value={selectedEntry.note}
                            onChange={e => updateSelectedFamily({ note: e.target.value })}
                            autoSize={{ minRows: 3, maxRows: 16 }}
                            placeholder="Clinical guidance for the LLM. Explain when to upgrade, downgrade, or keep."
                          />
                        ) : (
                          selectedEntry.note ? (
                            <div style={{
                              padding: '12px 14px',
                              background: '#FAFAFA',
                              border: '1px solid #E5E7EB',
                              borderRadius: 6,
                              fontSize: 13,
                              color: '#1F2937',
                              whiteSpace: 'pre-wrap',
                              lineHeight: 1.55,
                            }}>
                              {selectedEntry.note}
                            </div>
                          ) : (
                            <Text style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>
                              No note defined.
                            </Text>
                          )
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}

        </Content>
      </Layout>

      {/* ── Add-new family modal ────────────────────────────────────── */}
      <Modal
        title="Add new family"
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onOk={handleAddSubmit}
        okText="Add"
        cancelText="Cancel"
        destroyOnClose
        width={560}
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Family ID"
            required
            validateStatus={newError ? 'error' : undefined}
            help={newError || 'Short code prefix, e.g. "E11", "N18", "F32".'}
          >
            <Input
              value={newFamilyId}
              onChange={e => { setNewFamilyId(e.target.value); if (newError) setNewError(null); }}
              placeholder="e.g. E11"
              style={{ fontFamily: 'monospace', fontWeight: 600 }}
              autoFocus
            />
          </Form.Item>
          <Form.Item label="Description">
            <TextArea
              value={newDescription}
              onChange={e => setNewDescription(e.target.value)}
              rows={2}
              placeholder="Short description of the family"
            />
          </Form.Item>
          <div style={{
            padding: '10px 12px',
            background: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: 6,
          }}>
            <Text style={{ fontSize: 12, color: '#6B7280' }}>
              New family starts with empty lists for trigger codes, candidate siblings, evidence sections,
              and screener keywords. <code>require_keyword_match</code> is initialized to <code>false</code>.
            </Text>
          </div>
        </Form>
      </Modal>

    </Layout>
  );
};

export default SpecificityTargetsConfig;